/**
 * KnowledgeObjectDetailPage — full drill-in page for a single Knowledge Object.
 *
 * Route: /knowledge-bases/:kbId/objects/:koId
 *
 * Layout:
 *   - Compact header: filename, kind, status, layer counts, actions
 *   - Toolbar: filter toggle, search (inline quick-filter), expand-all toggle,
 *              active-filter badges
 *   - Body: scrollable unit list (left) + optional filter panel (right)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  Tag,
  IconButton,
  InlineLoading,
  Button,
  Modal,
} from '@carbon/react';
import {
  Download,
  Reset,
  TrashCan,
  Filter,
  ChevronDown,
  ChevronRight,
  Document,
  Table,
  ChartLine,
  Api,
  DataBase,
  Layers,
  SidePanelOpen,
  SidePanelClose,
} from '@carbon/icons-react';
import {
  useKnowledgeBase,
  useKBDocuments,
  useKnowledgeUnits,
  useKnowledgeUnitLayers,
  useRechunkKnowledgeObject,
  useProcessLayers,
  useDeleteDocument,
  useKnowledgeObjectConcepts,
} from '@/lib/query/hooks';
import { knowledgeBaseApi } from '@/lib/api';
import KnowledgeUnitCard from '@/components/knowledge-base/KnowledgeUnitCard';
import KnowledgeFilterPanel, {
  defaultFilters,
  type KnowledgeFilters,
  type ExtractionKind,
} from '@/components/knowledge-base/KnowledgeFilterPanel';
import PdfProvenanceViewer from '@/components/knowledge-base/PdfProvenanceViewer';
import '@/components/knowledge-base/PdfProvenanceViewer.scss';
import type { KnowledgeUnit, KnowledgeObjectKind, UnitProvenance } from '@shared/types/knowledge-base';
import { deriveKOStatus } from '@shared/utils/knowledge-object-status';
import './KnowledgeObjectDetailPage.scss';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function KindIcon({ kind }: { kind: KnowledgeObjectKind }) {
  switch (kind) {
    case 'document': return <Document size={16} />;
    case 'table': return <Table size={16} />;
    case 'timeseries': return <ChartLine size={16} />;
    case 'api_endpoint': return <Api size={16} />;
    case 'dataset': return <DataBase size={16} />;
    default: return <Document size={16} />;
  }
}

/**
 * Client-side filter logic.
 * A unit passes when:
 *  - Its type is in the selected types set
 *  - At least one selected layer applies:
 *      L1 → unit has content
 *      L2 → unit has a summary
 *      L3 → unit has extractions
 *  - The search term is found in content or label (case-insensitive)
 */
function applyFilters(
  units: KnowledgeUnit[],
  summaryMap: Map<string, unknown>,
  extractionMap: Map<string, Array<{ extraction_kind?: string }>>,
  filters: KnowledgeFilters
): KnowledgeUnit[] {
  const q = filters.search.trim().toLowerCase();
  return units.filter((u) => {
    // Type filter
    if (!filters.types.has(u.unit_type)) return false;

    // Extraction-kind filter (when L3 is active and extractionKinds is set)
    const unitExtractions = extractionMap.get(u.id) ?? [];
    if (filters.layers.has('L3') && filters.extractionKinds !== null && unitExtractions.length > 0) {
      const hasMatchingKind = unitExtractions.some((e) =>
        filters.extractionKinds!.has((e.extraction_kind ?? '') as ExtractionKind)
      );
      // If the unit has extractions but none match the selected kinds, exclude it
      // (units without extractions are still shown via L1/L2 match below)
      if (!hasMatchingKind) return false;
    }

    // Layer filter — at least one active layer must apply to this unit
    const unitHasL1 = !!u.content;
    const unitHasL2 = summaryMap.has(u.id);
    const unitHasL3 = unitExtractions.length > 0;
    const layerMatch =
      (filters.layers.has('L1') && unitHasL1) ||
      (filters.layers.has('L2') && unitHasL2) ||
      (filters.layers.has('L3') && unitHasL3);
    if (!layerMatch) return false;

    // Search filter
    if (q) {
      const inContent = (u.content ?? '').toLowerCase().includes(q);
      const inLabel = (u.label ?? '').toLowerCase().includes(q);
      if (!inContent && !inLabel) return false;
    }

    return true;
  });
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function KnowledgeObjectDetailPage() {
  const { kbId = '', koId = '' } = useParams<{ kbId: string; koId: string }>();
  const navigate = useNavigate();
  const { hash } = useLocation();

  // KU id encoded in the URL hash: /knowledge-bases/:kbId/objects/:koId#<kuId>
  const targetKuId = hash ? hash.slice(1) : null;
  // Tracks whether we have already scrolled to the target unit this load.
  const scrolledRef = useRef(false);

  const { data: kb } = useKnowledgeBase(kbId);
  const { data: documents } = useKBDocuments(kbId);
  const { data: units, isLoading: unitsLoading } = useKnowledgeUnits(koId);
  const { data: layers } = useKnowledgeUnitLayers(koId, true, units?.length);
  const { data: groundedConcepts } = useKnowledgeObjectConcepts(koId);
  const rechunkMutation = useRechunkKnowledgeObject(kbId);
  const processLayersMutation = useProcessLayers(kbId);
  const deleteMutation = useDeleteDocument(kbId);

  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filters, setFilters] = useState<KnowledgeFilters>(defaultFilters);
  const [expandAll, setExpandAll] = useState(false);
  const [openUnit, setOpenUnit] = useState<KnowledgeUnit | null>(null);
  // KU ids that are force-expanded via deep-link (distinct from expandAll).
  const [expandedByLink, setExpandedByLink] = useState<Set<string>>(new Set());
  // Ref to the scrollable list container — used for deep-link scrolling.
  const listWrapRef = useRef<HTMLDivElement>(null);

  // PDF pane state
  const [pdfPaneOpen, setPdfPaneOpen] = useState(false);
  const [pdfProvenance, setPdfProvenance] = useState<UnitProvenance | null>(null);

  // "Locate in PDF" — navigates the PDF pane to this unit's provenance page.
  // Opens the pane if it is closed. Does NOT open the detail modal.
  const handleUnitLocate = useCallback((unit: KnowledgeUnit) => {
    const prov = unit.metadata?.provenance as UnitProvenance | undefined;
    // Pass [] (not null) when a unit has no provenance so the viewer shows
    // "No location" instead of silently staying on page 1 without feedback.
    // null is reserved for the initial state (no unit selected at all).
    setPdfProvenance(prov ?? []);
    setPdfPaneOpen(true);
  }, []);

  // Called when the user clicks the card header (expand/collapse toggle).
  // When the PDF pane is already open we also locate the unit in the PDF so
  // that expanding a card always syncs the viewer — no need to hunt for the
  // small DocumentView button.
  const handleUnitExpand = useCallback((unit: KnowledgeUnit) => {
    if (pdfPaneOpen) {
      handleUnitLocate(unit);
    }
  }, [pdfPaneOpen, handleUnitLocate]);

  // "Open details" — opens the detail modal only. No PDF side-effects.
  const handleUnitOpenDetails = useCallback((unit: KnowledgeUnit) => {
    setOpenUnit(unit);
  }, []);

  // Find the KO from the already-loaded documents list (avoids a second API call)
  const ko = useMemo(
    () => (documents ?? []).find((d) => d.id === koId),
    [documents, koId]
  );

  // Build lookup maps for L2/L3
  const summaryByUnitId = useMemo(() => {
    const m = new Map<string, NonNullable<typeof layers>['summaries'][number]>();
    if (layers) for (const s of layers.summaries) m.set(s.knowledge_unit_id, s);
    return m;
  }, [layers]);

  const extractionsByUnitId = useMemo(() => {
    const m = new Map<string, NonNullable<typeof layers>['extractions']>();
    if (layers) {
      for (const e of layers.extractions) {
        const arr = m.get(e.knowledge_unit_id) ?? [];
        arr.push(e);
        m.set(e.knowledge_unit_id, arr);
      }
    }
    return m;
  }, [layers]);

  // Apply filters
  const filteredUnits = useMemo(
    () =>
      units
        ? applyFilters(units, summaryByUnitId, extractionsByUnitId, filters)
        : [],
    [units, summaryByUnitId, extractionsByUnitId, filters]
  );

  // Deep-link: once the filtered list is painted and a target KU id is in the
  // hash, expand the matching card and scroll it into view inside the list
  // container (which is the overflow-y:auto scroll root, not the document).
  // Depends on filteredUnits (not units) so the <li id> elements are already
  // in the DOM when the scroll fires. scrolledRef prevents re-firing on every
  // unrelated re-render.
  useEffect(() => {
    if (!targetKuId || scrolledRef.current || !filteredUnits.length) return;
    const match = filteredUnits.find((u) => u.id === targetKuId);
    if (!match) return;
    scrolledRef.current = true;
    setExpandedByLink((prev) => new Set([...prev, targetKuId]));
    // Two frames: first frame renders the expanded card, second frame lets
    // the browser compute the element's final position after expansion.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = listWrapRef.current;
        const el = container ? container.querySelector<HTMLElement>(`#${CSS.escape(targetKuId)}`) : null;
        if (!el || !container) return;
        const containerTop = container.getBoundingClientRect().top;
        const elTop = el.getBoundingClientRect().top;
        container.scrollBy({ top: elTop - containerTop - 16, behavior: 'smooth' });
      });
    });
  }, [targetKuId, filteredUnits]);

  // Layer counts for header
  const l1 = ko?.knowledge_unit_count ?? 0;
  const l2 = ko?.summary_count ?? 0;
  const l3 = ko?.extraction_count ?? 0;

  const handleDownload = async () => {
    if (!ko) return;
    try {
      const result = await knowledgeBaseApi.getDocumentMarkdown(ko.id);
      const content = (result as any).content ?? result;
      const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = ko.filename.replace(/\.[^.]+$/, '.md');
      window.document.body.appendChild(a);
      a.click();
      setTimeout(() => { window.document.body.removeChild(a); URL.revokeObjectURL(url); }, 150);
    } catch (err) {
      console.error('Failed to download markdown:', err);
    }
  };

  const handleDelete = async () => {
    if (!ko) return;
    if (!window.confirm(`Delete "${ko.filename}"? This cannot be undone.`)) return;
    // `submit` resolves with null instead of throwing, and only after verification has decided
    // whether a lost response actually deleted the row. Navigating away on the truth, not on
    // the transport.
    const deleted = await deleteMutation.submit(ko.id);
    if (deleted) navigate(`/knowledge-bases/${kbId}`);
    // Staying put IS the message on this page: the row is still listed, and the KB detail page is
    // where a delete has a notification to show. Reading `deleteMutation.error` here would print
    // the value captured at render time, which is still null.
    else window.alert('Could not delete this document. It is still there; try again.');
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="ko-detail">
      {/* ── Breadcrumb ─────────────────────────────────────────────────── */}
      <div className="ko-detail__breadcrumb">
        <Breadcrumb noTrailingSlash>
          <BreadcrumbItem onClick={() => navigate('/knowledge-bases')}>
            Knowledge Bases
          </BreadcrumbItem>
          <BreadcrumbItem onClick={() => navigate(`/knowledge-bases/${kbId}`)}>
            {kb?.name ?? '…'}
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            {ko?.filename ?? '…'}
          </BreadcrumbItem>
        </Breadcrumb>
      </div>

      {/* ── Compact header ─────────────────────────────────────────────── */}
      <div className="ko-detail__header">
        <div className="ko-detail__header-left">
          <span className="ko-detail__kind-icon">
            <KindIcon kind={(ko?.kind ?? 'document') as KnowledgeObjectKind} />
          </span>
          <div className="ko-detail__title-block">
            <h1 className="ko-detail__title">{ko?.filename ?? '…'}</h1>
            <div className="ko-detail__meta">
              {ko && (
                <>
                  <Tag type={deriveKOStatus(ko.conversion_status, l1, l2, l3, ko.layers_status).tagType} size="sm">
                    {deriveKOStatus(ko.conversion_status, l1, l2, l3, ko.layers_status).label}
                  </Tag>
                  <span className="ko-detail__meta-sep">·</span>
                  <span className="ko-detail__meta-text">{formatFileSize(ko.file_size)}</span>
                  <span className="ko-detail__meta-sep">·</span>
                  {l1 > 0 && (
                    <span className="ko-detail__layer-badge" title="L1 — Raw content chunks">
                      L1 · {l1}
                    </span>
                  )}
                  {l2 > 0 && (
                    <span className="ko-detail__layer-badge ko-detail__layer-badge--l2" title="L2 — AI-generated summaries">
                      L2 · {l2}
                    </span>
                  )}
                  {l3 > 0 && (
                    <span className="ko-detail__layer-badge ko-detail__layer-badge--l3" title="L3 — Structured extractions">
                      L3 · {l3}
                    </span>
                  )}
                  <span className="ko-detail__meta-sep">·</span>
                  <span className="ko-detail__meta-text">
                    uploaded {new Date(ko.uploaded_at).toLocaleDateString()}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="ko-detail__header-actions">
          {ko?.conversion_status === 'completed' && (
            <>
              <IconButton kind="ghost" size="sm" label="Download as Markdown" onClick={handleDownload}>
                <Download size={16} />
              </IconButton>
              <IconButton
                kind="ghost"
                size="sm"
                label="Re-run L2/L3 layers (summaries + extractions)"
                disabled={processLayersMutation.isPending && processLayersMutation.variables === koId}
                onClick={() => processLayersMutation.mutate(koId)}
              >
                {processLayersMutation.isPending && processLayersMutation.variables === koId
                  ? <InlineLoading style={{ width: '16px' }} />
                  : <Layers size={16} />}
              </IconButton>
              <IconButton
                kind="ghost"
                size="sm"
                label="Re-chunk knowledge units"
                disabled={rechunkMutation.isPending && rechunkMutation.variables === koId}
                onClick={() => rechunkMutation.mutate(koId)}
              >
                <Reset size={16} />
              </IconButton>
            </>
          )}
          <IconButton
            kind="ghost"
            size="sm"
            label="Delete document"
            onClick={handleDelete}
          >
            <TrashCan size={16} style={{ color: 'var(--cds-support-error)' }} />
          </IconButton>
        </div>
      </div>

      {/* ── Concepts grounded in this document ─────────────────────────── */}
      {groundedConcepts && groundedConcepts.length > 0 && (
        <div className="ko-detail__concepts" style={{ padding: '0.5rem 0 0.75rem' }}>
          <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.35rem' }}>
            Ontology concepts in this document ({groundedConcepts.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
            {groundedConcepts.map((c) => (
              <Tag
                key={c.ontology_node_id}
                type="purple"
                size="sm"
                title={`${c.grounding_count} matching chunk(s), best similarity ${c.best_score.toFixed(
                  2
                )}${c.exploration_count > 1 ? `, in ${c.exploration_count} explorations` : ''}`}
              >
                {c.label} · {c.best_score.toFixed(2)}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div className="ko-detail__toolbar">
        <Button
          kind={filterPanelOpen ? 'primary' : 'ghost'}
          size="sm"
          renderIcon={Filter}
          iconDescription="Toggle filters"
          onClick={() => setFilterPanelOpen((v) => !v)}
        >
          Filters
          {filters.search || filters.layers.size < 3 || filters.types.size < 8 || filters.extractionKinds !== null
            ? ` (active)`
            : ''}
        </Button>

        <Button
          kind="ghost"
          size="sm"
          renderIcon={expandAll ? ChevronDown : ChevronRight}
          iconDescription={expandAll ? 'Collapse all' : 'Expand all'}
          onClick={() => setExpandAll((v) => !v)}
        >
          {expandAll ? 'Collapse all' : 'Expand all'}
        </Button>

        <span className="ko-detail__toolbar-spacer" />

        {/* PDF pane toggle — only for document-kind KOs */}
        {ko?.kind === 'document' && ko?.conversion_status === 'completed' && (
          <Button
            kind={pdfPaneOpen ? 'primary' : 'ghost'}
            size="sm"
            renderIcon={pdfPaneOpen ? SidePanelClose : SidePanelOpen}
            iconDescription={pdfPaneOpen ? 'Close PDF pane' : 'Open PDF pane'}
            onClick={() => setPdfPaneOpen((v) => !v)}
          >
            {pdfPaneOpen ? 'Hide PDF' : 'View PDF'}
          </Button>
        )}

        <span className="ko-detail__unit-count">
          {unitsLoading ? (
            <InlineLoading description="Loading…" />
          ) : (
            <>
              {filteredUnits.length !== (units?.length ?? 0) ? (
                <>{filteredUnits.length} of {units?.length ?? 0} units</>
              ) : (
                <>{units?.length ?? 0} unit{(units?.length ?? 0) !== 1 ? 's' : ''}</>
              )}
            </>
          )}
        </span>
      </div>

      {/* ── Body: unit list + optional filter panel + PDF pane ──────────── */}
      <div className={`ko-detail__body${pdfPaneOpen ? ' ko-detail__body--pdf-open' : ''}`}>
        <div className="ko-detail__list-wrap" ref={listWrapRef}>
          {unitsLoading ? (
            <div className="ko-detail__loading">
              <InlineLoading description="Loading knowledge units…" />
            </div>
          ) : filteredUnits.length === 0 ? (
            <div className="ko-detail__empty">
              {(units?.length ?? 0) === 0
                ? 'No knowledge units yet. Use "Re-chunk" to generate them.'
                : 'No units match the current filters.'}
            </div>
          ) : (
            <ol className="ko-detail__list">
              {filteredUnits.map((unit) => (
                <li key={unit.id} id={unit.id}>
                  <KnowledgeUnitCard
                    unit={unit}
                    summary={summaryByUnitId.get(unit.id)}
                    extractions={extractionsByUnitId.get(unit.id)}
                    forceExpanded={expandAll || expandedByLink.has(unit.id) ? true : undefined}
                    knowledgeObjectId={koId}
                    onLocate={ko?.kind === 'document' && ko?.conversion_status === 'completed'
                      ? () => handleUnitLocate(unit)
                      : undefined}
                    onExpand={ko?.kind === 'document' && ko?.conversion_status === 'completed'
                      ? () => handleUnitExpand(unit)
                      : undefined}
                    onOpen={() => handleUnitOpenDetails(unit)}
                  />
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Filter panel slides in from the right */}
        <KnowledgeFilterPanel
          open={filterPanelOpen}
          filters={filters}
          onChange={setFilters}
          onClose={() => setFilterPanelOpen(false)}
          totalCount={units?.length ?? 0}
          filteredCount={filteredUnits.length}
        />

        {/* PDF pane */}
        {pdfPaneOpen && ko && (
          <div className="ko-detail__pdf-pane">
            <PdfProvenanceViewer
              knowledgeObjectId={koId}
              provenance={pdfProvenance}
              onPageChange={() => {/* no-op: page is internal to the viewer */}}
            />
          </div>
        )}
      </div>

      {/* ── Knowledge Unit detail modal ─────────────────────────────────── */}
      {openUnit && (
        <Modal
          open
          passiveModal
          size="lg"
          modalHeading={
            openUnit.label
              ? `KU #${openUnit.ordinal} — ${openUnit.label}`
              : `Knowledge Unit #${openUnit.ordinal}`
          }
          onRequestClose={() => setOpenUnit(null)}
          className="ko-detail__ku-modal"
        >
          <div className="ko-detail__ku-modal-body">
            <KnowledgeUnitCard
              unit={openUnit}
              summary={summaryByUnitId.get(openUnit.id)}
              extractions={extractionsByUnitId.get(openUnit.id)}
              forceExpanded
              knowledgeObjectId={koId}
              layout="rows"
            />
          </div>
        </Modal>
      )}
    </div>
  );
}

// Made with Bob
